import {Html, OrbitControls} from '@react-three/drei';
import {Canvas} from '@react-three/fiber';
import {XR} from '@react-three/xr';
import {Suspense, useRef} from 'react';
import {Camera} from 'three';
import {xrStore} from '../../store/xrStore';
import {Layer} from '../../types/layers';
import {logger} from '../../utils/logger';

interface VRPreviewSceneProps {
  children: React.ReactNode;
  eye?: 'left' | 'right';
}

export const VRPreviewScene: React.FC<VRPreviewSceneProps> = ({children, eye = 'left'}) => {
  const cameraRef = useRef<Camera | null>(null);

  return (
    <Canvas
      camera={{
        position: [0, 0.9, 0],
        fov: 60,
        near: 0.1,
        far: 1000,
      }}
      gl={{
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      }}
      style={{background: '#000000', width: '100%', height: '100%'}}
      dpr={[1, 2]}
      onCreated={({camera, gl}) => {
        cameraRef.current = camera;
        camera.layers.enableAll();
        if (eye === 'left') {
          camera.layers.disable(Layer.RIGHT);
        } else {
          camera.layers.disable(Layer.LEFT);
        }

        // Add context loss handling
        gl.domElement.addEventListener('webglcontextlost', event => {
          event.preventDefault();
          logger.error('WebGL context lost in VRPreviewScene');
        });

        gl.domElement.addEventListener('webglcontextrestored', () => {
          logger.info('WebGL context restored in VRPreviewScene');
          camera.layers.enableAll();
          if (eye === 'left') {
            camera.layers.disable(Layer.RIGHT);
          } else {
            camera.layers.disable(Layer.LEFT);
          }
        });
      }}
    >
      <XR store={xrStore}>
        <Suspense
          fallback={
            <Html center>
              <div style={{color: 'white', fontSize: '24px'}}>Loading...</div>
            </Html>
          }
        >
          <ambientLight intensity={0.1} />
          <directionalLight position={[10, 10, 5]} intensity={0.5} />
          <group position={[0, -0.5, -2]}>{children}</group>
          <OrbitControls
            target={[0, 1, -2]}
            enableDamping={false}
            enableZoom={true}
            enablePan={true}
            minDistance={1}
            maxDistance={10}
          />
        </Suspense>
      </XR>
    </Canvas>
  );
};
